import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FolderPlus, Plus, Loader2, ShieldCheck, ShieldAlert, ShieldX, ArrowLeft, Clock } from "lucide-react";
import { usePrograms, type ProgramRecord } from "@/hooks/usePrograms";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface ScanAlert {
  code: string;
  message: string;
  severity: "low" | "medium" | "high";
  rule: string;
}

interface ScanResult {
  scan_id: string | null;
  rule_score: number;
  ai_score: number | null;
  risk_level: "low" | "moderate" | "high";
  passed: boolean;
  alerts: ScanAlert[];
}

interface AddToProgramDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  sessionTitle: string;
}

export function AddToProgramDialog({ open, onOpenChange, sessionId, sessionTitle }: AddToProgramDialogProps) {
  const { programs, linkSessionToProgram, createEmptyProgram } = usePrograms();
  const [mode, setMode] = useState<"pick" | "create" | "scan">("pick");
  const [newTitle, setNewTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [selectedProgram, setSelectedProgram] = useState<ProgramRecord | null>(null);
  const [note, setNote] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");

  const activePrograms = programs.filter((p) => p.status !== "archived");

  const runScan = async (programId: string): Promise<ScanResult | null> => {
    setScanning(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Session expirée, reconnecte-toi");
        return null;
      }

      const resp = await supabase.functions.invoke("scan-session", {
        body: { program_id: programId, session_id: sessionId },
      });

      if (resp.error) {
        console.error("Scan error:", resp.error);
        toast.error("Erreur lors du scan");
        return null;
      }

      return resp.data as ScanResult;
    } catch (e) {
      console.error("Scan exception:", e);
      toast.error("Erreur lors du scan");
      return null;
    } finally {
      setScanning(false);
    }
  };

  const handleSelectProgram = async (program: ProgramRecord) => {
    setSelectedProgram(program);
    setMode("scan");
    const result = await runScan(program.id);
    setScanResult(result);
  };

  const handleConfirmAdd = async () => {
    if (!selectedProgram) return;
    setLoading(true);
    const ok = await linkSessionToProgram(
      sessionId,
      selectedProgram.id,
      undefined,
      note.trim() || undefined,
      scheduledTime || null
    );
    setLoading(false);
    if (ok) {
      toast.success(`Séance ajoutée à "${selectedProgram.title}"`);
      resetAndClose();
    }
  };

  const handleCreateNew = async () => {
    if (!newTitle.trim()) {
      toast.error("Titre requis");
      return;
    }
    setLoading(true);
    const program = await createEmptyProgram(newTitle.trim());
    if (program) {
      const ok = await linkSessionToProgram(sessionId, program.id, undefined, undefined, scheduledTime || null);
      if (ok) {
        toast.success(`Programme "${newTitle}" créé avec la séance`);
      }
    }
    setLoading(false);
    resetAndClose();
  };

  const resetAndClose = () => {
    onOpenChange(false);
    setNewTitle("");
    setNote("");
    setScheduledTime("");
    setMode("pick");
    setScanResult(null);
    setSelectedProgram(null);
  };

  const riskIcon = (level: string) => {
    if (level === "low") return <ShieldCheck className="h-4 w-4 text-green-500" />;
    if (level === "moderate") return <ShieldAlert className="h-4 w-4 text-yellow-500" />;
    return <ShieldX className="h-4 w-4 text-destructive" />;
  };

  const riskLabel = (level: string) => {
    if (level === "low") return "Risque faible";
    if (level === "moderate") return "Risque modéré";
    return "Risque élevé";
  };

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sm">
            Ajouter « {sessionTitle} » à un programme
          </DialogTitle>
        </DialogHeader>

        {mode === "pick" && (
          <div className="space-y-3">
            {activePrograms.length > 0 ? (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {activePrograms.map((p) => (
                  <button
                    key={p.id}
                    disabled={loading || scanning}
                    onClick={() => handleSelectProgram(p)}
                    className="w-full rounded-lg border border-border bg-card p-3 text-left hover:border-primary/40 transition-all flex items-center gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{p.title}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {format(new Date(p.created_at), "d MMM yyyy", { locale: fr })} · {p.duration_weeks} sem.
                      </p>
                    </div>
                    <Badge variant="outline" className="text-[10px] shrink-0">
                      {p.status === "draft" ? "Brouillon" : "Sauvé"}
                    </Badge>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-4">
                Aucun programme actif. Crée-en un nouveau.
              </p>
            )}

            <Button
              variant="outline"
              className="w-full text-xs gap-1.5"
              onClick={() => setMode("create")}
            >
              <Plus className="h-3.5 w-3.5" />
              Créer un nouveau programme
            </Button>
          </div>
        )}

        {mode === "scan" && (
          <div className="space-y-3">
            {scanning ? (
              <div className="flex flex-col items-center gap-2 py-6">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <p className="text-xs text-muted-foreground">Analyse de compatibilité…</p>
              </div>
            ) : scanResult ? (
              <>
                {/* Score header */}
                <div className="flex items-center gap-2 p-3 rounded-lg border border-border bg-card">
                  {riskIcon(scanResult.risk_level)}
                  <div className="flex-1">
                    <p className="text-sm font-medium">{riskLabel(scanResult.risk_level)}</p>
                    <p className="text-[11px] text-muted-foreground">
                      Score : {scanResult.rule_score}/100
                      {scanResult.ai_score !== null && ` · Cerveau : ${scanResult.ai_score}/100`}
                    </p>
                  </div>
                  <Badge
                    variant={scanResult.passed ? "outline" : "destructive"}
                    className="text-[10px]"
                  >
                    {scanResult.passed ? "Compatible" : "Bloqué"}
                  </Badge>
                </div>

                {/* Alerts */}
                {scanResult.alerts.length > 0 && (
                  <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
                    {scanResult.alerts.map((alert, i) => (
                      <Alert
                        key={i}
                        variant={alert.severity === "high" ? "destructive" : "default"}
                        className="py-2 px-3"
                      >
                        <AlertDescription className="text-[11px]">
                          {alert.message}
                        </AlertDescription>
                      </Alert>
                    ))}
                  </div>
                )}

                {/* Scheduled time */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Heure prévue (optionnel)
                  </label>
                  <Input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="text-sm w-32"
                  />
                </div>

                {/* Note */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Note (optionnel)</label>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Ex : Séance du lundi, focus pecs…"
                    className="text-sm min-h-[60px]"
                    rows={2}
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="text-xs gap-1"
                    onClick={() => { setMode("pick"); setScanResult(null); setSelectedProgram(null); }}
                  >
                    <ArrowLeft className="h-3 w-3" />
                    Retour
                  </Button>
                  <Button
                    className="flex-1 text-xs"
                    onClick={handleConfirmAdd}
                    disabled={!scanResult.passed || loading}
                  >
                    {loading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                    ) : (
                      <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                    )}
                    {scanResult.passed ? "Confirmer l'ajout" : "Ajout impossible"}
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-xs text-muted-foreground">Erreur lors de l'analyse. Réessaie.</p>
                <Button
                  variant="outline"
                  className="mt-2 text-xs"
                  onClick={() => { setMode("pick"); setScanResult(null); setSelectedProgram(null); }}
                >
                  Retour
                </Button>
              </div>
            )}
          </div>
        )}

        {mode === "create" && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Nom du programme</label>
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Ex : Programme force 4 semaines"
                className="text-sm"
                autoFocus
              />
            </div>
            {/* Scheduled time for new program too */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Heure prévue (optionnel)
              </label>
              <Input
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                className="text-sm w-32"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="text-xs" onClick={() => setMode("pick")}>
                Retour
              </Button>
              <Button className="flex-1 text-xs" onClick={handleCreateNew} disabled={loading || !newTitle.trim()}>
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <FolderPlus className="h-3.5 w-3.5 mr-1" />}
                Créer et ajouter
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
